/**
 * Criterion mutation actions used by actionable protocol validation findings.
 */

import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type {
  ProtocolCriterion,
  ProtocolDraftBundle,
  ProtocolValidationRun,
} from "../../api/clinicalTrialsApiTypes";

type SetError = (value: string | null) => void;
type SetBusy = (value: boolean) => void;
type SetValidation = (value: ProtocolValidationRun | null) => void;

/**
 * Save a criterion rationale, reload the draft, and refresh validation.
 *
 * @param trialId - Owning trial identifier
 * @param facilityId - Facility authorization boundary
 * @param bundle - Current draft bundle
 * @param criterion - Criterion being updated
 * @param rationale - Human-authored clinical rationale
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param setValidation - Latest-validation setter
 * @param loadTrial - Draft reload callback
 * @returns Nothing
 * @throws Error when the criterion update fails
 */
export async function saveCriterionRationale(
  trialId: string,
  facilityId: string,
  bundle: ProtocolDraftBundle | null,
  criterion: ProtocolCriterion,
  rationale: string,
  setBusy: SetBusy,
  setError: SetError,
  setValidation: SetValidation,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!bundle) throw new Error("Protocol draft is not loaded");
  setBusy(true);
  setError(null);
  try {
    await clinicalTrialsService.upsertProtocolCriterion(
      trialId,
      bundle.draft.draft_id,
      {
        confirmation: true,
        expected_revision: bundle.draft.revision,
        criterion: {
          ...criterion,
          trial_id: trialId,
          facility_id: facilityId,
          rationale,
        },
      },
    );
    const nextValidation = await clinicalTrialsService.validateProtocolDraft(
      trialId,
      bundle.draft.draft_id,
    );
    setValidation(nextValidation);
    await loadTrial();
  } catch (error) {
    setError(
      error instanceof Error
        ? error.message
        : "Failed to save criterion rationale",
    );
    throw error;
  } finally {
    setBusy(false);
  }
}
